import { useState } from 'react';
import { useDealershipCityByDn } from '../../../hooks/useDealershipCityByDn';
import type { Occurrence, OccurrenceWithTimeSpent } from '../../../models/occurrence';
import { convertTimeStringToMilliseconds } from '../../../utils/dateUtils';
import { getCurrentStep, getOccurrenceTypeLabelId, getStepLabelId } from '../trackingUtils';
import type { SummaryProps } from '../types';
import CriticalityBadge from './CriticalityBadge';
import OccurrenceSummaryDialog from './OccurrenceSummaryDialog';
import useMtText from './useMtText';

interface IKanbanVehicleCardProps {
    occurrence: Occurrence;
    timeSpent?: OccurrenceWithTimeSpent;
    summaryProps: SummaryProps;
}

const getEstimatedTotalMs = (occurrence: Occurrence) =>
    (occurrence.steps ?? []).reduce(
        (total, step) => total + convertTimeStringToMilliseconds(step.estimatedTime ?? '00:00'),
        0
    );

const KanbanVehicleCard = ({ occurrence, timeSpent, summaryProps }: IKanbanVehicleCardProps) => {
    const { msg, formatNumber, formatDuration } = useMtText();
    const [showSummary, setShowSummary] = useState(false);
    const city = useDealershipCityByDn(occurrence.dealershipDn, occurrence.dealershipLocation);
    const typeLabelId = getOccurrenceTypeLabelId(occurrence.occurrenceType);
    const currentStep = getCurrentStep(occurrence);
    const finalized = !!occurrence.finished;
    const stepLabel = finalized
        ? msg('intl-msg:occurrence.card.completedStatus')
        : currentStep
          ? msg(getStepLabelId(currentStep))
          : msg('intl-msg:occurrence.card.notStarted');
    const model = occurrence.vehicleModel ?? occurrence.model ?? '-';
    const client = occurrence.accountName || occurrence.customer;
    const dealership = occurrence.dealershipName
        ? `${occurrence.dealershipName} - DN: ${occurrence.dealershipDn ?? '-'}`
        : '-';
    const hasHourmeter = occurrence.vehicleHourmeter !== null && occurrence.vehicleHourmeter !== undefined;

    return (
        <>
            <button type='button' className='mt-kvc' onClick={() => setShowSummary(true)}>
                <span className='mt-kvc__top'>
                    <b>
                        <span className='rioglyph rioglyph-truck-baseline' />
                        {occurrence.vehicleLicensePlate || occurrence.vehicleChassis}
                    </b>
                    <CriticalityBadge value={occurrence.criticality} />
                </span>
                <span className='mt-kr'>
                    <span className='mt-mono'>{occurrence.vehicleChassis}</span>
                    <span>{occurrence.vehicleLicensePlate}</span>
                </span>
                <span className='mt-kr'>
                    <span>
                        {msg('intl-msg:kanban.vehicleCards.odometer', {
                            value:
                                occurrence.vehicleOdometer !== undefined
                                    ? formatNumber(occurrence.vehicleOdometer)
                                    : '-',
                        })}
                    </span>
                    {hasHourmeter && (
                        <span>
                            {msg('intl-msg:kanban.vehicleCards.hourmeter', {
                                value: formatNumber(Number(occurrence.vehicleHourmeter)),
                            })}
                        </span>
                    )}
                </span>
                <span className='mt-kr'>
                    <span>{msg('intl-msg:kanban.vehicleCards.vehicleModel')}</span>
                    <span title={model}>{model}</span>
                </span>
                <span className='mt-kr'>
                    <span>{msg('intl-msg:occurrence.card.estimatedTime')}</span>
                    <span className='mt-num'>
                        <span className='rioglyph rioglyph-time-alt' />
                        {formatDuration(getEstimatedTotalMs(occurrence))}
                    </span>
                </span>
                <span className='mt-kr'>
                    <span>{msg('intl-msg:kanban.vehicleCards.totalTime')}</span>
                    <span className='mt-num mt-kr__strong'>
                        <span className='rioglyph rioglyph-time-alt' />
                        {formatDuration((timeSpent?.totalSecondsCard ?? 0) * 1000)}
                    </span>
                </span>
                {client && (
                    <span className='mt-kr mt-kr__client' title={client}>
                        {client}
                    </span>
                )}
                <span className='mt-kr'>
                    <span>{msg('intl-msg:kanban.vehicleCards.dealership')}</span>
                    <span title={dealership}>{dealership}</span>
                </span>
                <span className='mt-kr'>
                    <span>{msg('intl-msg:kanban.vehicleCards.dnCity')}</span>
                    <span title={city}>{city || '-'}</span>
                </span>
                <span className='mt-kr'>
                    <span>{msg('intl-msg:kanban.vehicleCards.currentStep')}</span>
                    <span title={stepLabel}>{stepLabel}</span>
                </span>
                {typeLabelId && (
                    <span className='mt-kr'>
                        <span>{msg('intl-msg:occurrence.card.type')}</span>
                        <span>{msg(typeLabelId)}</span>
                    </span>
                )}
            </button>

            <OccurrenceSummaryDialog
                occurrence={occurrence}
                summaryProps={summaryProps}
                show={showSummary}
                onClose={() => setShowSummary(false)}
            />
        </>
    );
};

export default KanbanVehicleCard;
